import { useState, useEffect, useRef, useCallback } from 'react';

export const ConnectionStatus = {
  IDLE: 'IDLE',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  ERROR: 'ERROR',
};

/**
 * Manages a Strophe.js XMPP connection lifecycle.
 * Dynamically imports strophe.js to avoid SSR issues.
 * Accepts a latestPasswordRef so reconnects can use a refreshed token
 * without triggering re-renders that tear down the active connection.
 */
export function useChatConnection(
  websocketUrl,
  jid,
  password,
  latestPasswordRef,
) {
  const [status, setStatus] = useState(ConnectionStatus.IDLE);
  const connRef = useRef(null);
  const mountedRef = useRef(true);
  const stropheRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!websocketUrl || !jid || !password) {
      setStatus(ConnectionStatus.IDLE);
      return;
    }

    if (typeof window === 'undefined') return;

    let cancelled = false;

    async function connect() {
      setStatus(ConnectionStatus.CONNECTING);

      const stropheModule = await import('strophe.js');
      stropheRef.current = stropheModule;
      const { Strophe } = stropheModule;

      if (cancelled || !mountedRef.current) return;

      // Disconnect any existing connection
      if (connRef.current) {
        try {
          connRef.current.disconnect();
        } catch {
          // ignore
        }
      }

      const conn = new Strophe.Connection(websocketUrl);
      connRef.current = conn;

      conn.connect(jid, password, (connStatus) => {
        if (cancelled || !mountedRef.current) return;

        switch (connStatus) {
          case Strophe.Status.CONNECTING:
            setStatus(ConnectionStatus.CONNECTING);
            break;
          case Strophe.Status.CONNECTED:
            // Send initial presence to become "available" on the server.
            // XMPP RFC 6121 requires this before any MUC interaction.
            try {
              conn.send(new Strophe.Builder('presence'));
            } catch {
              // ignore — connection error will be reported via next status
            }
            setStatus(ConnectionStatus.CONNECTED);
            break;
          case Strophe.Status.DISCONNECTED:
            setStatus(ConnectionStatus.DISCONNECTED);
            break;
          case Strophe.Status.AUTHFAIL:
          case Strophe.Status.CONNFAIL:
          case Strophe.Status.ERROR:
            setStatus(ConnectionStatus.ERROR);
            break;
          default:
            break;
        }
      });
    }

    connect();

    return () => {
      cancelled = true;
      if (connRef.current) {
        try {
          connRef.current.disconnect();
        } catch {
          // ignore
        }
        connRef.current = null;
      }
    };
  }, [websocketUrl, jid, password]);

  const retry = useCallback(async () => {
    if (!websocketUrl || !jid) return;
    if (typeof window === 'undefined') return;

    setStatus(ConnectionStatus.CONNECTING);

    const stropheModule = stropheRef.current || (await import('strophe.js'));
    stropheRef.current = stropheModule;
    const { Strophe } = stropheModule;

    // Use refreshed password if available
    const pw = latestPasswordRef?.current || password;

    // Create fresh connection (old one may be in bad state)
    if (connRef.current) {
      try {
        connRef.current.disconnect();
      } catch {
        // ignore
      }
    }

    const conn = new Strophe.Connection(websocketUrl);
    connRef.current = conn;

    conn.connect(jid, pw, (connStatus) => {
      if (!mountedRef.current) return;
      switch (connStatus) {
        case Strophe.Status.CONNECTED:
          try {
            conn.send(new Strophe.Builder('presence'));
          } catch {
            // ignore
          }
          setStatus(ConnectionStatus.CONNECTED);
          break;
        case Strophe.Status.DISCONNECTED:
          setStatus(ConnectionStatus.DISCONNECTED);
          break;
        case Strophe.Status.AUTHFAIL:
        case Strophe.Status.CONNFAIL:
        case Strophe.Status.ERROR:
          setStatus(ConnectionStatus.ERROR);
          break;
        default:
          break;
      }
    });
  }, [websocketUrl, jid, password, latestPasswordRef]);

  return {
    status,
    connection: connRef.current,
    retry,
  };
}
